// @flow
import React from 'react';
import { Icon, Button, Modal, Toggle, Input } from '@elements';
import { openInBrowser } from '@helpers/cordova';
import { normalizeVideoLink, isVideoLink } from '@helpers/videoLink';
import GoogleMeetCard from './GoogleMeetCard';
import '../Styles/OnlineModal.scss';

type Props = {
  online: boolean,
  videoUrl: ?string,
  doctorLink: ?string, // the professional's fixed room in this clinic, used when the appointment has no link of its own
  googleMeet?: boolean, // the professional connected Google Agenda: saved without a link, it gets a Google Meet link
  doctorId?: ?(number | string), // the appointment's professional, for GoogleMeetCard
  onChange: Function,
  setModal: Function,
  _: Function,
};

// An online appointment: the patient's WhatsApp confirmation, page and email carry the call link instead of the address
const OnlineModal = ({
  online, videoUrl, doctorLink, googleMeet = false, doctorId = null, onChange, setModal, _,
}: Props) => {
  const ownLink = String(videoUrl || '').trim();
  const invalid = ownLink !== '' && !isVideoLink(ownLink);
  const callLink = ownLink ? (!invalid && normalizeVideoLink(ownLink)) : doctorLink;
  // Once the API created it, the Meet link is the appointment's own link, shown as any other
  const meetOnSave = googleMeet && !ownLink;

  // Saved with a Meet link: GoogleMeetCard says so instead
  let hint = _('label/online/no_link');
  if (ownLink) hint = _('label/online/own_link');
  else if (meetOnSave) hint = null;
  else if (doctorLink) hint = `${_('label/online/uses_doctor_link')} ${doctorLink}`;

  let placeholder = doctorLink || 'https://meet.google.com/abc-defg-hij';
  if (meetOnSave) placeholder = _('label/online/google_meet_placeholder');

  return (
    <Modal id="onlineModal" className="secondaryModal">
      <div className="modalHeader actionHeader">
        <Button className="backButton" onClick={() => setModal(null)}>
          <Icon iconName="arrow-left" className="icon" />
        </Button>
        <span className="backButtonLabel">{_('label/online/title')}</span>
      </div>
      <div className="onlineModalBody">
        <Toggle name="online" label={_('label/online/toggle')} isChecked={online} onChange={onChange} />
        {online && (
          <React.Fragment>
            <Input
              type="text"
              inputMode="url"
              name="videoUrl"
              label={_('label/online/link')}
              placeholder={placeholder}
              value={ownLink}
              error={invalid ? _('error/online/invalid_link') : ''}
              onChange={onChange}
            />
            {hint && <p className="onlineHint">{hint}</p>}
            {/* A link of its own is the patient's: Google creates none */}
            {!ownLink && <GoogleMeetCard doctorId={doctorId} googleMeet={googleMeet} roomLink={Boolean(doctorLink)} _={_} />}
            {callLink && (
              <Button className="onlineJoinButton" onClick={() => openInBrowser(callLink)}>
                <Icon iconName="videocam" size={20} />
                <span>{_('action/online/join')}</span>
              </Button>
            )}
            <p className="onlineHint">{_('label/online/patient_gets_link')}</p>
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
};

export default OnlineModal;
